export const SUPPORTED_LOCALES = [
  "en",
  "es",
  "pt-BR",
  "fr",
  "de",
  "th",
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_STORAGE_KEY = "streaminal.locale";

const SUPPORTED_SET = new Set<string>(SUPPORTED_LOCALES);

const isSupported = (value: string): value is SupportedLocale =>
  SUPPORTED_SET.has(value);

const normalize = (tag: string): SupportedLocale | null => {
  const lower = tag.toLowerCase();

  // Portuguese: always collapse to pt-BR for this launch
  if (lower === "pt" || lower.startsWith("pt-")) return "pt-BR";

  // Exact match on full tag (case-insensitive for the non-region part)
  for (const locale of SUPPORTED_LOCALES) {
    if (locale.toLowerCase() === lower) return locale;
  }

  // Base-language match (strip region)
  const base = lower.split("-")[0];
  if (isSupported(base)) return base as SupportedLocale;

  return null;
};

export const detectLanguage = (): SupportedLocale => {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && isSupported(stored)) return stored;
  } catch {
    // localStorage unavailable (SSR, privacy mode) — fall through
  }

  const languages = navigator.languages ?? [];
  for (const lang of languages) {
    const match = normalize(lang);
    if (match) return match;
  }

  return "en";
};
